/**
 * @fileoverview Tracking API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Tracking module.
 * Single source of truth for all tracking-related API definitions.
 */

import { z } from 'zod';
import {
	createContract,
	extractRoutes,
	ApiSuccessResponseSchema,
	ApiPaginatedResponseSchema,
} from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Tracking Category Enum
 * High-level grouping of events
 */
export enum TrackingCategory {
	// Core
	SYSTEM = 'system',
	NAVIGATION = 'navigation',
	ENGAGEMENT = 'engagement',

	// User Journey
	ONBOARDING = 'onboarding',
	WIZARD = 'wizard',
	TEST = 'test',

	// Monetization
	PAYMENT = 'payment',
	SUBSCRIPTION = 'subscription',

	// Marketing
	CAMPAIGN = 'campaign',
	SEO = 'seo',

	// Errors
	ERROR = 'error',
}

/**
 * Tracking Action Enum
 * Specific action taken by the user or system
 */
export enum TrackingAction {
	VIEW = 'view',
	CLICK = 'click',
	SUBMIT = 'submit',
	START = 'start',
	COMPLETE = 'complete',
	FAIL = 'fail',
	CANCEL = 'cancel',
	SEARCH = 'search',
	FILTER = 'filter',
	SORT = 'sort',
	DOWNLOAD = 'download',
	UPLOAD = 'upload',
	LOGIN = 'login',
	LOGOUT = 'logout',
	SIGNUP = 'signup',
	UPDATE = 'update',
	DELETE = 'delete',
	OPEN = 'open',
	CLOSE = 'close',
}

/**
 * Tracking Label Enum
 * Common labels for standard components/actions.
 * Note: Custom labels are still permitted via string union in strict mode,
 * but this provides a standard vocabulary.
 */
export enum TrackingLabel {
	// Buttons
	CTA_PRIMARY = 'cta_primary',
	CTA_SECONDARY = 'cta_secondary',
	SUBMIT_BUTTON = 'submit_button',
	CANCEL_BUTTON = 'cancel_button',
	BACK_BUTTON = 'back_button',
	NEXT_BUTTON = 'next_button',

	// Navigation
	HEADER_LINK = 'header_link',
	FOOTER_LINK = 'footer_link',
	SIDEBAR_LINK = 'sidebar_link',

	// Features
	THEME_TOGGLE = 'theme_toggle',
	LANGUAGE_SELECTOR = 'language_selector',
	USER_MENU = 'user_menu',
}

/**
 * Tracking Event Type Enum
 */
export enum TrackingEventType {
	// Core tracking events
	TRACKING_STARTED = 'tracking_started',
	PAGE_VIEW = 'page_view',
	PAGE_ENGAGEMENT = 'page_engagement',
	PAGE_PERFORMANCE = 'page_performance',

	// User interaction events
	CTA_CLICKED = 'cta_clicked',
	FORM_INTERACTION = 'form_interaction',
	NAVIGATION_CLICK = 'navigation_click',

	// Onboarding events
	ONBOARDING_STEP_STARTED = 'onboarding_step_started',
	ONBOARDING_STEP_COMPLETED = 'onboarding_step_completed',
	ONBOARDING_COMPLETED = 'onboarding_completed',

	// Wizard events
	WIZARD_PHASE1_STARTED = 'wizard_phase1_started',
	WIZARD_PHASE1_COMPLETED = 'wizard_phase1_completed',
	WIZARD_PHASE2_STARTED = 'wizard_phase2_started',
	WIZARD_PHASE2_COMPLETED = 'wizard_phase2_completed',
	WIZARD_COMPLETED = 'wizard_completed',

	// Test session events
	TEST_STARTED = 'test_started',
	TEST_STEP_COMPLETED = 'test_step_completed',
	TEST_PAUSED = 'test_paused',
	TEST_RESUMED = 'test_resumed',
	TEST_COMPLETED = 'test_completed',
	TEST_ABANDONED = 'test_abandoned',

	// Payment events
	PAYMENT_INITIATED = 'payment_initiated',
	PAYMENT_COMPLETED = 'payment_completed',
	PAYMENT_FAILED = 'payment_failed',

	// Analysis events
	ANALYSIS_REQUESTED = 'analysis_requested',
	ANALYSIS_VIEWED = 'analysis_viewed',
	ANALYSIS_DOWNLOADED = 'analysis_downloaded',

	// Experiment/A-B testing events
	VARIANT_ASSIGNED = 'variant_assigned',
	CONVERSION_GOAL_REACHED = 'conversion_goal_reached',

	// Custom events
	CUSTOM_EVENT = 'custom_event',

	// Error events
	ERROR_OCCURRED = 'error_occurred',
	API_ERROR = 'api_error',
}

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Start Tracking Request Schema
 */
export const StartTrackingSchema = z.object({
	pageUrl: z.string().optional(),
	referrer: z.string().optional(),
	utmSource: z.string().optional(),
	utmMedium: z.string().optional(),
	utmCampaign: z.string().optional(),
	utmContent: z.string().optional(),
	utmTerm: z.string().optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type StartTrackingPayload = z.infer<typeof StartTrackingSchema>;

/**
 * Start Tracking Response Schema
 */
export const StartTrackingResponseSchema = z.object({
	sessionId: z.string(),
	message: z.string(),
});

export const StartTrackingApiResponseSchema = ApiSuccessResponseSchema(StartTrackingResponseSchema);
export type StartTrackingResponse = z.infer<typeof StartTrackingApiResponseSchema>;

/**
 * Track Event Request Schema
 */
export const TrackEventSchema = z.object({
	eventType: z.enum([
		TrackingEventType.TRACKING_STARTED,
		TrackingEventType.PAGE_VIEW,
		TrackingEventType.PAGE_ENGAGEMENT,
		TrackingEventType.PAGE_PERFORMANCE,
		TrackingEventType.CTA_CLICKED,
		TrackingEventType.FORM_INTERACTION,
		TrackingEventType.NAVIGATION_CLICK,
		TrackingEventType.ONBOARDING_STEP_STARTED,
		TrackingEventType.ONBOARDING_STEP_COMPLETED,
		TrackingEventType.ONBOARDING_COMPLETED,
		TrackingEventType.WIZARD_PHASE1_STARTED,
		TrackingEventType.WIZARD_PHASE1_COMPLETED,
		TrackingEventType.WIZARD_PHASE2_STARTED,
		TrackingEventType.WIZARD_PHASE2_COMPLETED,
		TrackingEventType.WIZARD_COMPLETED,
		TrackingEventType.TEST_STARTED,
		TrackingEventType.TEST_STEP_COMPLETED,
		TrackingEventType.TEST_PAUSED,
		TrackingEventType.TEST_RESUMED,
		TrackingEventType.TEST_COMPLETED,
		TrackingEventType.TEST_ABANDONED,
		TrackingEventType.PAYMENT_INITIATED,
		TrackingEventType.PAYMENT_COMPLETED,
		TrackingEventType.PAYMENT_FAILED,
		TrackingEventType.ANALYSIS_REQUESTED,
		TrackingEventType.ANALYSIS_VIEWED,
		TrackingEventType.ANALYSIS_DOWNLOADED,
		TrackingEventType.VARIANT_ASSIGNED,
		TrackingEventType.CONVERSION_GOAL_REACHED,
		TrackingEventType.CUSTOM_EVENT,
		TrackingEventType.ERROR_OCCURRED,
		TrackingEventType.API_ERROR,
	]),
	eventData: z.record(z.string(), z.unknown()).optional(),
	category: z.nativeEnum(TrackingCategory).optional().or(z.string().max(100)), // Allow string for backward compat, but prefer Enum
	action: z.nativeEnum(TrackingAction).optional().or(z.string().max(100)),
	label: z.nativeEnum(TrackingLabel).optional().or(z.string().max(255)), // Flexible for specific IDs
	value: z
		.number()
		.int('Value must be an integer')
		.min(-2147483648, 'Value must be >= -2147483648 (PostgreSQL integer min)')
		.max(2147483647, 'Value must be <= 2147483647 (PostgreSQL integer max)')
		.optional(),
	pageUrl: z.string().optional(),
	referrer: z.string().optional(),
	fingerprint: z.string().optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type TrackEventPayload = z.infer<typeof TrackEventSchema>;

/**
 * Tracking Event Entity Schema (API Response)
 *
 * NOTE: userId is NOT included here because it should be accessed through
 * the session relation in the database (trackingEvent.session.userId).
 * The session_tracking table is the single source of truth for userId.
 */
export const TrackingEventSchema = z.object({
	id: z.uuid(),
	sessionId: z.string(),
	eventType: z.enum([
		TrackingEventType.TRACKING_STARTED,
		TrackingEventType.PAGE_VIEW,
		TrackingEventType.PAGE_ENGAGEMENT,
		TrackingEventType.PAGE_PERFORMANCE,
		TrackingEventType.CTA_CLICKED,
		TrackingEventType.FORM_INTERACTION,
		TrackingEventType.NAVIGATION_CLICK,
		TrackingEventType.ONBOARDING_STEP_STARTED,
		TrackingEventType.ONBOARDING_STEP_COMPLETED,
		TrackingEventType.ONBOARDING_COMPLETED,
		TrackingEventType.WIZARD_PHASE1_STARTED,
		TrackingEventType.WIZARD_PHASE1_COMPLETED,
		TrackingEventType.WIZARD_PHASE2_STARTED,
		TrackingEventType.WIZARD_PHASE2_COMPLETED,
		TrackingEventType.WIZARD_COMPLETED,
		TrackingEventType.TEST_STARTED,
		TrackingEventType.TEST_STEP_COMPLETED,
		TrackingEventType.TEST_PAUSED,
		TrackingEventType.TEST_RESUMED,
		TrackingEventType.TEST_COMPLETED,
		TrackingEventType.TEST_ABANDONED,
		TrackingEventType.PAYMENT_INITIATED,
		TrackingEventType.PAYMENT_COMPLETED,
		TrackingEventType.PAYMENT_FAILED,
		TrackingEventType.ANALYSIS_REQUESTED,
		TrackingEventType.ANALYSIS_VIEWED,
		TrackingEventType.ANALYSIS_DOWNLOADED,
		TrackingEventType.VARIANT_ASSIGNED,
		TrackingEventType.CONVERSION_GOAL_REACHED,
		TrackingEventType.CUSTOM_EVENT,
		TrackingEventType.ERROR_OCCURRED,
		TrackingEventType.API_ERROR,
	]),
	category: z.string().nullable(),
	action: z.string().nullable(),
	label: z.string().nullable(),
	value: z.number().int().nullable(),
	pageUrl: z.string().nullable(),
	referrer: z.string().nullable(),
	ipAddress: z.string().nullable(),
	userAgent: z.string().nullable(),
	fingerprint: z.string().nullable(),
	metadata: z.record(z.string(), z.unknown()).nullable(),
	createdAt: z.string(), // ISO 8601 datetime
});
export type TrackingEvent = z.infer<typeof TrackingEventSchema>;

/**
 * Track Event Response Schema
 */
export const TrackEventResponseSchema = z.object({
	message: z.string(),
	sessionId: z.string().optional(),
});
export const TrackEventApiResponseSchema = ApiSuccessResponseSchema(TrackEventResponseSchema);
export type TrackEventApiResponse = z.infer<typeof TrackEventApiResponseSchema>;

// ============================================================================
// SPECIFIC EVENT TYPE SCHEMAS
// ============================================================================

/**
 * Base Tracking Event Schema
 */
export const BaseTrackingEventSchema = z.object({
	eventType: z.enum([
		TrackingEventType.TRACKING_STARTED,
		TrackingEventType.PAGE_VIEW,
		TrackingEventType.PAGE_ENGAGEMENT,
		TrackingEventType.PAGE_PERFORMANCE,
		TrackingEventType.CTA_CLICKED,
		TrackingEventType.FORM_INTERACTION,
		TrackingEventType.NAVIGATION_CLICK,
		TrackingEventType.ONBOARDING_STEP_STARTED,
		TrackingEventType.ONBOARDING_STEP_COMPLETED,
		TrackingEventType.ONBOARDING_COMPLETED,
		TrackingEventType.WIZARD_PHASE1_STARTED,
		TrackingEventType.WIZARD_PHASE1_COMPLETED,
		TrackingEventType.WIZARD_PHASE2_STARTED,
		TrackingEventType.WIZARD_PHASE2_COMPLETED,
		TrackingEventType.WIZARD_COMPLETED,
		TrackingEventType.TEST_STARTED,
		TrackingEventType.TEST_STEP_COMPLETED,
		TrackingEventType.TEST_PAUSED,
		TrackingEventType.TEST_RESUMED,
		TrackingEventType.TEST_COMPLETED,
		TrackingEventType.TEST_ABANDONED,
		TrackingEventType.PAYMENT_INITIATED,
		TrackingEventType.PAYMENT_COMPLETED,
		TrackingEventType.PAYMENT_FAILED,
		TrackingEventType.ANALYSIS_REQUESTED,
		TrackingEventType.ANALYSIS_VIEWED,
		TrackingEventType.ANALYSIS_DOWNLOADED,
		TrackingEventType.VARIANT_ASSIGNED,
		TrackingEventType.CONVERSION_GOAL_REACHED,
		TrackingEventType.CUSTOM_EVENT,
		TrackingEventType.ERROR_OCCURRED,
		TrackingEventType.API_ERROR,
	]),
	timestamp: z.iso.datetime(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type BaseTrackingEvent = z.infer<typeof BaseTrackingEventSchema>;

/**
 * CTA Click Event Schema
 */
export const CTAClickEventSchema = BaseTrackingEventSchema.extend({
	eventType: z.literal(TrackingEventType.CTA_CLICKED),
	ctaType: z.string(),
	ctaLocation: z.string().optional(),
	ctaText: z.string().optional(),
});
export type CTAClickEvent = z.infer<typeof CTAClickEventSchema>;

/**
 * Onboarding Event Schema
 */
export const OnboardingEventSchema = BaseTrackingEventSchema.extend({
	eventType: z.enum([
		TrackingEventType.ONBOARDING_STEP_STARTED,
		TrackingEventType.ONBOARDING_STEP_COMPLETED,
		TrackingEventType.ONBOARDING_COMPLETED,
	]),
	stepNumber: z.number().int().optional(),
	stepName: z.string().optional(),
});
export type OnboardingEvent = z.infer<typeof OnboardingEventSchema>;

/**
 * Wizard Event Schema
 */
export const WizardEventSchema = BaseTrackingEventSchema.extend({
	eventType: z.enum([
		TrackingEventType.WIZARD_PHASE1_STARTED,
		TrackingEventType.WIZARD_PHASE1_COMPLETED,
		TrackingEventType.WIZARD_PHASE2_STARTED,
		TrackingEventType.WIZARD_PHASE2_COMPLETED,
		TrackingEventType.WIZARD_COMPLETED,
	]),
	phase: z.number().int().optional(),
	step: z.number().int().optional(),
});
export type WizardEvent = z.infer<typeof WizardEventSchema>;

/**
 * Monetization Event Schema
 */
export const MonetizationEventSchema = BaseTrackingEventSchema.extend({
	eventType: z.enum([
		TrackingEventType.PAYMENT_INITIATED,
		TrackingEventType.PAYMENT_COMPLETED,
		TrackingEventType.PAYMENT_FAILED,
	]),
	amount: z.number().optional(),
	currency: z.string().optional(),
	paymentIntentId: z.string().optional(),
});
export type MonetizationEvent = z.infer<typeof MonetizationEventSchema>;

/**
 * Engagement Event Schema
 */
export const EngagementEventSchema = BaseTrackingEventSchema.extend({
	eventType: z.enum([
		TrackingEventType.PAGE_VIEW,
		TrackingEventType.PAGE_ENGAGEMENT,
		TrackingEventType.NAVIGATION_CLICK,
	]),
	pageUrl: z.string().optional(),
	duration: z.number().optional(),
});
export type EngagementEvent = z.infer<typeof EngagementEventSchema>;

/**
 * Performance Event Schema
 */
export const PerformanceEventSchema = BaseTrackingEventSchema.extend({
	eventType: z.enum([TrackingEventType.PAGE_PERFORMANCE, TrackingEventType.ERROR_OCCURRED]),
	loadTime: z.number().optional(),
	errorMessage: z.string().optional(),
	errorStack: z.string().optional(),
});
export type PerformanceEvent = z.infer<typeof PerformanceEventSchema>;

/**
 * Experiment Event Schema
 */
export const ExperimentEventSchema = BaseTrackingEventSchema.extend({
	eventType: z.enum([
		TrackingEventType.VARIANT_ASSIGNED,
		TrackingEventType.CONVERSION_GOAL_REACHED,
	]),
	experimentId: z.string().optional(),
	variantId: z.string().optional(),
	goalName: z.string().optional(),
});
export type ExperimentEvent = z.infer<typeof ExperimentEventSchema>;

/**
 * Create Tracking Event DTO Schema
 */
export const CreateTrackingEventDtoSchema = z.union([
	CTAClickEventSchema,
	OnboardingEventSchema,
	WizardEventSchema,
	MonetizationEventSchema,
	EngagementEventSchema,
	PerformanceEventSchema,
	ExperimentEventSchema,
	BaseTrackingEventSchema,
]);
export type CreateTrackingEventDto = z.infer<typeof CreateTrackingEventDtoSchema>;

/**
 * Track Pageview Request Schema
 */
export const TrackPageviewSchema = z.object({
	pageUrl: z.string(),
	pageTitle: z.string().optional(),
	referrer: z.string().optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type TrackPageviewPayload = z.infer<typeof TrackPageviewSchema>;

/**
 * Batch Track Events Request Schema
 */
export const TrackEventsBatchSchema = z.object({
	events: z
		.array(TrackEventSchema)
		.min(1, 'At least one event is required')
		.max(100, 'Maximum 100 events per batch'),
});
export type TrackEventsBatchPayload = z.infer<typeof TrackEventsBatchSchema>;

/**
 * Batch Track Events Response Schema
 */
export const TrackEventsBatchResponseSchema = z.object({
	message: z.string(),
	processedCount: z.number().int(),
	failedCount: z.number().int(),
});
export const TrackEventsBatchApiResponseSchema = ApiSuccessResponseSchema(
	TrackEventsBatchResponseSchema,
);
export type TrackEventsBatchApiResponse = z.infer<typeof TrackEventsBatchApiResponseSchema>;

/**
 * Events Response Schema
 */
export const EventsResponseSchema = z.object({
	events: z.array(TrackingEventSchema),
	total: z.number().int().nonnegative(),
});

/**
 * List Events Query Schema (Admin)
 */
export const ListEventsQuerySchema = z.object({
	sessionId: z.string().optional(),
	userId: z.uuid().optional(),
	eventType: z
		.enum([
			TrackingEventType.TRACKING_STARTED,
			TrackingEventType.PAGE_VIEW,
			TrackingEventType.PAGE_ENGAGEMENT,
			TrackingEventType.PAGE_PERFORMANCE,
			TrackingEventType.CTA_CLICKED,
			TrackingEventType.FORM_INTERACTION,
			TrackingEventType.NAVIGATION_CLICK,
			TrackingEventType.ONBOARDING_STEP_STARTED,
			TrackingEventType.ONBOARDING_STEP_COMPLETED,
			TrackingEventType.ONBOARDING_COMPLETED,
			TrackingEventType.WIZARD_PHASE1_STARTED,
			TrackingEventType.WIZARD_PHASE1_COMPLETED,
			TrackingEventType.WIZARD_PHASE2_STARTED,
			TrackingEventType.WIZARD_PHASE2_COMPLETED,
			TrackingEventType.WIZARD_COMPLETED,
			TrackingEventType.TEST_STARTED,
			TrackingEventType.TEST_STEP_COMPLETED,
			TrackingEventType.TEST_PAUSED,
			TrackingEventType.TEST_RESUMED,
			TrackingEventType.TEST_COMPLETED,
			TrackingEventType.TEST_ABANDONED,
			TrackingEventType.PAYMENT_INITIATED,
			TrackingEventType.PAYMENT_COMPLETED,
			TrackingEventType.PAYMENT_FAILED,
			TrackingEventType.ANALYSIS_REQUESTED,
			TrackingEventType.ANALYSIS_VIEWED,
			TrackingEventType.ANALYSIS_DOWNLOADED,
			TrackingEventType.VARIANT_ASSIGNED,
			TrackingEventType.CONVERSION_GOAL_REACHED,
			TrackingEventType.CUSTOM_EVENT,
			TrackingEventType.ERROR_OCCURRED,
			TrackingEventType.API_ERROR,
		])
		.optional(),
	category: z.string().max(100).optional(),
	search: z.string().max(255).optional(),
	startDate: z.string().optional(),
	endDate: z.string().optional(),
	experimentId: z
		.string()
		.regex(/^[a-z0-9_]+$/)
		.max(100)
		.optional(),
	variantId: z
		.string()
		.regex(/^[a-z0-9_]+$/)
		.max(100)
		.optional(),
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(20),
});
export type ListEventsQuery = z.infer<typeof ListEventsQuerySchema>;

/**
 * Tracking Aggregates Query Schema (Admin)
 */
export const TrackingAggregatesQuerySchema = z.object({
	startDate: z.string().optional(),
	endDate: z.string().optional(),
	experimentId: z
		.string()
		.regex(/^[a-z0-9_]+$/)
		.max(100)
		.optional(),
	variantId: z
		.string()
		.regex(/^[a-z0-9_]+$/)
		.max(100)
		.optional(),
});
export type TrackingAggregatesQuery = z.infer<typeof TrackingAggregatesQuerySchema>;

/**
 * Event Type Count Schema (for aggregates)
 */
export const EventTypeCountSchema = z.object({
	eventType: z.string(),
	count: z.number().int().nonnegative(),
});
export type EventTypeCount = z.infer<typeof EventTypeCountSchema>;

/**
 * Top Page Schema (for aggregates)
 */
export const TopPageSchema = z.object({
	pageUrl: z.string(),
	count: z.number().int().nonnegative(),
});
export type TopPage = z.infer<typeof TopPageSchema>;

/**
 * Tracking Aggregates Response Schema
 */
export const TrackingAggregatesResponseSchema = z.object({
	totalEvents: z.number().int().nonnegative(),
	uniqueSessions: z.number().int().nonnegative(),
	eventsByType: z.array(EventTypeCountSchema),
	errorRate: z.number().min(0).max(100),
	topPages: z.array(TopPageSchema),
});
export type TrackingAggregatesResponse = z.infer<typeof TrackingAggregatesResponseSchema>;

export const TrackingAggregatesApiResponseSchema = ApiSuccessResponseSchema(
	TrackingAggregatesResponseSchema,
);
export type TrackingAggregatesApiResponse = z.infer<typeof TrackingAggregatesApiResponseSchema>;

export const PaginatedEventsApiResponseSchema = ApiPaginatedResponseSchema(TrackingEventSchema);
export type PaginatedEventsApiResponse = z.infer<typeof PaginatedEventsApiResponseSchema>;

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Tracking API Contract
 *
 * Defines the complete API surface for the Tracking module.
 * Both frontend and backend use this as the single source of truth.
 */
export const TRACKING_API_CONTRACT = createContract('/tracking', {
	/**
	 * POST /tracking/start
	 * Start tracking for a new visitor
	 */
	start: {
		method: 'POST' as const,
		path: '/start',
		body: StartTrackingSchema,
		response: StartTrackingApiResponseSchema,
	},

	/**
	 * POST /tracking/event
	 * Track a single event
	 */
	trackEvent: {
		method: 'POST' as const,
		path: '/event',
		body: TrackEventSchema,
		response: TrackEventApiResponseSchema,
	},

	/**
	 * POST /tracking/events/batch
	 * Track multiple events in a single request
	 */
	trackEventsBatch: {
		method: 'POST' as const,
		path: '/events/batch',
		body: TrackEventsBatchSchema,
		response: TrackEventsBatchApiResponseSchema,
	},

	/**
	 * POST /tracking/pageview
	 * Track a pageview
	 */
	trackPageview: {
		method: 'POST' as const,
		path: '/pageview',
		body: TrackPageviewSchema,
		response: TrackEventApiResponseSchema,
	},

	/**
	 * GET /tracking/admin/list
	 * List all tracking events (Admin only)
	 */
	adminListEvents: {
		method: 'GET' as const,
		path: '/admin/list',
		query: ListEventsQuerySchema,
		response: PaginatedEventsApiResponseSchema,
	},

	/**
	 * GET /tracking/admin/aggregates
	 * Get tracking event aggregates (Admin only)
	 */
	adminEventAggregates: {
		method: 'GET' as const,
		path: '/admin/aggregates',
		query: TrackingAggregatesQuerySchema,
		response: TrackingAggregatesApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const TRACKING_ROUTES = extractRoutes(TRACKING_API_CONTRACT);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type TrackingApiContract = typeof TRACKING_API_CONTRACT;
